import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, ExternalLink } from "lucide-react";
import { LegalHeader, LegalFooter } from "./legal/Terms";
import useSEO from "../lib/useSEO";

/**
 * /embed — public page listing three copy-paste HTML snippets that customers
 * can drop into their own sites, blogs, portfolios or case studies. Each
 * embed contains an anchor back to capittance.com — every install = a real
 * inbound backlink.
 */

const SNIPPETS = {
  badge: {
    label: "Powered-by badge · footer",
    preview: <BadgePreview />,
    html: `<!-- Capittance · Powered-by badge -->
<a href="https://capittance.com?utm_source=powered_by_badge" target="_blank" rel="noopener"
   style="display:inline-flex;align-items:center;gap:6px;padding:6px 10px;border:1px solid #E4E4E7;border-radius:2px;background:#FAFAFA;color:#0F172A;font:600 11px/1 Inter,system-ui,sans-serif;text-decoration:none">
  <span style="display:inline-block;width:8px;height:8px;border-radius:1px;background:#0369A1"></span>
  Powered by <b style="color:#0369A1">Capittance</b>
</a>`,
  },
  certificate: {
    label: "Certified Practitioner card",
    preview: <CertificatePreview />,
    html: `<!-- Capittance · Certified Practitioner -->
<div style="display:inline-block;padding:14px 18px;border:1px solid #E4E4E7;border-radius:4px;background:linear-gradient(135deg,#FEF7ED,#FEF3C7);font-family:Inter,system-ui,sans-serif">
  <div style="font:600 9px/1 Inter;text-transform:uppercase;letter-spacing:0.18em;color:#A16207">Certified Practitioner</div>
  <div style="font:800 18px/1.2 Inter;color:#0F172A;margin-top:4px">Capittance Harmonizer</div>
  <div style="font:400 11px/1.4 Inter;color:#52525B;margin-top:4px">SAP delivery on the Harmonizer platform</div>
  <a href="https://capittance.com?utm_source=certification" target="_blank" rel="noopener"
     style="display:inline-block;margin-top:8px;font:600 10px/1 Inter;color:#0369A1;text-decoration:none">
    capittance.com →
  </a>
</div>`,
  },
  testimonial: {
    label: "Testimonial block · case study",
    preview: <TestimonialPreview />,
    html: `<!-- Capittance · Testimonial block -->
<blockquote style="margin:0;padding:20px 24px;border-left:4px solid #7A1F2B;background:#FAFAFA;font-family:Inter,system-ui,sans-serif">
  <p style="font:400 15px/1.6 Inter;color:#0F172A;margin:0 0 12px">
    "[Your quote here] — Capittance Harmonizer turned three tools and a Sunday-night deck-build into one live workspace. Board packs in one click, no more Excel triage."
  </p>
  <footer style="font:600 12px/1 Inter;color:#52525B">
    — [Your name], [Your title]
    · <a href="https://capittance.com?utm_source=testimonial" style="color:#7A1F2B;text-decoration:none">Powered by Capittance</a>
  </footer>
</blockquote>`,
  },
};

export default function EmbedPage() {
  useSEO({
    title: "Embed widgets · Capittance badges, certificates, testimonials",
    description: "Free HTML embeds for customers, partners and case studies. Powered-by badges, certified-practitioner cards, and testimonial blocks — copy-paste ready.",
    path: "/embed",
  });
  return (
    <div className="min-h-screen bg-white">
      <LegalHeader title="Embeds" subtitle="Show off your Capittance credentials" />
      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="page-embed">
        <div className="mb-8">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">For customers, partners, consultants</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-zinc-900 leading-tight mt-2">Copy-paste HTML embeds</h1>
          <p className="text-base text-zinc-700 leading-relaxed mt-3 max-w-2xl">
            Three ready-to-use HTML snippets you can drop into your own website, blog footer, LinkedIn newsletter, case study, or partner page. No JavaScript, no dependencies — just inline styles, so it works everywhere.
          </p>
        </div>

        {Object.entries(SNIPPETS).map(([key, s]) => (
          <SnippetCard key={key} testid={key} label={s.label} preview={s.preview} html={s.html}/>
        ))}

        <div className="card p-5 mt-8 bg-zinc-50" data-testid="embed-guidelines">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Guidelines</div>
          <ul className="text-sm text-zinc-700 space-y-1 list-disc pl-5">
            <li>Please keep the <code className="text-xs bg-white px-1 border border-[var(--border)] rounded-sm">capittance.com</code> link intact and follow-worthy (no <code>nofollow</code> unless required by your platform).</li>
            <li>You can restyle colours / fonts to match your site — keep the brand mark visible.</li>
            <li>Not for competitors or affiliates of competing PPM/SAP-tool platforms.</li>
            <li>Questions or custom sizes? <a href="mailto:Requests@Capittance.com?subject=Embed%20widget%20request" className="underline">Requests@Capittance.com</a></li>
          </ul>
        </div>
        <LegalFooter />
      </main>
    </div>
  );
}

function SnippetCard({ label, preview, html, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(html); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* noop */ }
  };
  return (
    <div className="card p-5 mb-6" data-testid={`embed-card-${testid}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-display text-lg font-extrabold tracking-tight">{label}</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Preview + copy-paste HTML</div>
        </div>
        <button onClick={copy} className={`btn btn-sm inline-flex items-center gap-1.5 ${copied ? "btn-secondary" : "btn-primary"}`} data-testid={`embed-copy-${testid}`}>
          {copied ? <><Check size={12}/> Copied</> : <><Copy size={12}/> Copy HTML</>}
        </button>
      </div>
      <div className="bg-zinc-50 border border-[var(--border)] rounded-sm p-4 mb-3" data-testid={`embed-preview-${testid}`}>{preview}</div>
      <pre className="text-[10px] font-mono bg-zinc-900 text-zinc-100 rounded-sm p-3 overflow-x-auto leading-relaxed" data-testid={`embed-html-${testid}`}>
        {html}
      </pre>
    </div>
  );
}

// ── Preview components ────────────────────────────────────────────────────
function BadgePreview() {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px", border: "1px solid #E4E4E7", borderRadius: 2, background: "#FAFAFA", color: "#0F172A", font: '600 11px/1 Inter,system-ui,sans-serif' }}>
      <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 1, background: "#0369A1" }} />
      Powered by <b style={{ color: "#0369A1" }}>Capittance</b>
    </span>
  );
}
function CertificatePreview() {
  return (
    <div style={{ display: "inline-block", padding: "14px 18px", border: "1px solid #E4E4E7", borderRadius: 4, background: "linear-gradient(135deg,#FEF7ED,#FEF3C7)", fontFamily: "Inter,system-ui,sans-serif" }}>
      <div style={{ font: "600 9px/1 Inter", textTransform: "uppercase", letterSpacing: "0.18em", color: "#A16207" }}>Certified Practitioner</div>
      <div style={{ font: "800 18px/1.2 Inter", color: "#0F172A", marginTop: 4 }}>Capittance Harmonizer</div>
      <div style={{ font: "400 11px/1.4 Inter", color: "#52525B", marginTop: 4 }}>SAP delivery on the Harmonizer platform</div>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 3, marginTop: 8, font: "600 10px/1 Inter", color: "#0369A1" }}>capittance.com <ExternalLink size={10}/></span>
    </div>
  );
}
function TestimonialPreview() {
  return (
    <blockquote style={{ margin: 0, padding: "20px 24px", borderLeft: "4px solid #7A1F2B", background: "#FAFAFA", fontFamily: "Inter,system-ui,sans-serif" }}>
      <p style={{ font: "400 15px/1.6 Inter", color: "#0F172A", margin: "0 0 12px" }}>
        "[Your quote here] — Capittance Harmonizer turned three tools and a Sunday-night deck-build into one live workspace. Board packs in one click, no more Excel triage."
      </p>
      <footer style={{ font: "600 12px/1 Inter", color: "#52525B" }}>
        — [Your name], [Your title] · <span style={{ color: "#7A1F2B" }}>Powered by Capittance</span>
      </footer>
    </blockquote>
  );
}
